import queryString from 'query-string';
import url from 'url';
import moment from 'moment';
import lodashIntersection from 'lodash/fp/intersection';

import typeCheck from 'common/util/typeCheck';

import {
  getListingsMinMaxValues,
  isSelfShowingListing,
  hasListing3DTour,
} from 'common/listingHelpers';

import {
  numberWithCommas,
} from 'common/formatters';

import {
  LISTING_STATUS_ACTIVE,
} from 'common/listingConstants';

import {
  PROPERTY_ATTRIBUTE_CATEGORY_LISTING_FIELD,
  PROPERTY_ATTRIBUTE_CATEGORY_FEATURE,
} from 'common/propertyAttributeConstants';

export const APARTMENT_YARD_SIGN_REDIRECTION_UTM_QUERY_PARAMETERS = (
  'utm_source=leasing&utm_medium=real-world&utm_campaign=self-tour&utm_content=yard-sign'
);

export function getApartmentFieldsForDisplay({
  apartmentBuildings,
  apartmentListings,
  floorPlans = [],
  propertyAttributes,
}) {
  typeCheck('apartmentBuildings::Array', apartmentBuildings);
  typeCheck('propertyAttributes::Array', propertyAttributes);
  typeCheck('floorPlans::Array', floorPlans);

  const defaultApartmentBuilding = apartmentBuildings[0] || {};
  const listingFields = propertyAttributes
    .filter(({ category }) => (
      category === PROPERTY_ATTRIBUTE_CATEGORY_LISTING_FIELD
    ))
    .reduce((memo, { name, attrs }) => ({
      ...memo,
      [name]: attrs.value,
    }), {});
  const propertyFeatures = propertyAttributes.filter(({ category }) => (
    category === PROPERTY_ATTRIBUTE_CATEGORY_FEATURE
  ));

  const { minPrice, maxPrice } = getListingsMinMaxValues(apartmentListings.filter((listing) => {
    return (
      floorPlans.length ? !!listing.floorPlanId : true
    ) && listing.price;
  }));
  const priceRange = numberWithCommas(minPrice) + (maxPrice !== minPrice
    ? ' - ' + numberWithCommas(maxPrice)
    : ''
  );

  const apartmentListingsByFloorPlanId = apartmentListings.reduce((memo, listing) => {
    if (listing.floorPlanId) {
      memo[listing.floorPlanId] = memo[listing.floorPlanId] || [];
      memo[listing.floorPlanId].push(listing);
    }

    return memo;
  }, {});

  const floorPlansCount = floorPlans.filter((floorPlan) => {
    return (
      floorPlan.tourableApartmentUnits.length
      && apartmentListingsByFloorPlanId[floorPlan.id]
      && floorPlan.tourableApartmentUnits.find((apartmentUnit) => {
        return apartmentListingsByFloorPlanId[floorPlan.id].find((unit) => {
          return (
            unit.apartmentUnit === apartmentUnit
            && unit.status === LISTING_STATUS_ACTIVE
          );
        });
      })
    );
  }).length;

  const hasSelfShowingUnits = floorPlans.filter((floorPlan) => {
    return (
      floorPlan.tourableApartmentUnits.length
      && apartmentListingsByFloorPlanId[floorPlan.id]
      && floorPlan.tourableApartmentUnits.find((apartmentUnit) => {
        return apartmentListingsByFloorPlanId[floorPlan.id].find((unit) => {
          return (
            unit.apartmentUnit === apartmentUnit
            && unit.status === LISTING_STATUS_ACTIVE
            && isSelfShowingListing(unit)
          );
        });
      })
    );
  }).length > 0;

  const has3DTourUnits = floorPlans.filter((floorPlan) => {
    return (
      floorPlan.tourableApartmentUnits.length
      && apartmentListingsByFloorPlanId[floorPlan.id]
      && floorPlan.tourableApartmentUnits.find((apartmentUnit) => {
        return apartmentListingsByFloorPlanId[floorPlan.id].find((unit) => {
          return (
            unit.apartmentUnit === apartmentUnit
            && unit.status === LISTING_STATUS_ACTIVE
            && hasListing3DTour(unit)
          );
        });
      })
    );
  }).length > 0;

  const fieldsForDisplay = {
    // TODO: Rename this field. This is not the full apartment's address display.
    address: [
      `${defaultApartmentBuilding.addressCity}`,
      `${defaultApartmentBuilding.addressStateCode} ${defaultApartmentBuilding.addressZip}`,
    ].join(', '),
    addressStreetName: defaultApartmentBuilding.addressStreetName,
    addressStreetNumber: defaultApartmentBuilding.addressStreetNumber,
    addressStreetSuffix: defaultApartmentBuilding.addressStreetSuffix,
    priceRange: `$${priceRange}`,
    locationLatitude: defaultApartmentBuilding.locationLatitude,
    locationLongitude: defaultApartmentBuilding.locationLongitude,
    activeListings: apartmentListings.filter(({ status }) => status === LISTING_STATUS_ACTIVE).length,
    floorPlansCount: floorPlansCount,
    listingFields: listingFields,
    propertyFeatures: propertyFeatures,
    apartmentBuildingIds: apartmentBuildings.map(({ id }) => id),
    hasSelfShowingUnits: hasSelfShowingUnits,
    has3DTourUnits: has3DTourUnits,
  };

  return fieldsForDisplay;
}

export function getApartmentUrlPath(apartment, queryParams) {
  typeCheck('apartment::NonEmptyObject', apartment);
  typeCheck('queryParams::Maybe Object', queryParams);

  let querySearch = queryParams ? `?${queryString.stringify(queryParams)}` : '';
  if (querySearch === '?') {
    querySearch = '';
  }

  return `/apartment/${apartment.id}${querySearch}`;
}

export function getApartmentPageMetadata({ apartment, appUrl }) {
  const {
    address,
    priceRange,
  } = apartment.fieldsForDisplay;

  return {
    pageTitle: `${address} | Sunroom Rentals `,
    section: 'apartment',
    canonical: url.resolve(appUrl, getApartmentUrlPath(apartment)),
    keywords: (`rent, home, apartment, austin, texas, tour, ${address}`).toLowerCase(),
    description: (
      `Sunroom has ${apartment.photoUrls.length} photos of this ${priceRange} ` +
      `apartment with many floorplans at ${address} - ${apartment.name} available for move in now`
    ),
    image: apartment.photoUrls[0],
  };
}

export function getApartmentCoverPhotoUrl(apartment) {
  return apartment && apartment.photoUrls && apartment.photoUrls[0] || null;
}

export function getActiveApartmentSpecialDeals({
  apartmentSpecialDeals,
  floorPlanId,
  apartmentUnits = [],
}) {
  const nowMoment = moment();

  return apartmentSpecialDeals
    .filter((apartmentSpecialDeal) => {
      return (
        (
          (apartmentSpecialDeal.applicableApartmentUnits.length === 0 && apartmentUnits.length === 0)
          || lodashIntersection(apartmentSpecialDeal.applicableApartmentUnits, apartmentUnits).length > 0
        )
        && (
          (!apartmentSpecialDeal.floorPlanId && !floorPlanId)
          || apartmentSpecialDeal.floorPlanId === floorPlanId
        )
        && (
          !apartmentSpecialDeal.endDate
          || nowMoment.isBefore(apartmentSpecialDeal.endDate)
        )
      );
    });
}

export function getListingSpecialDeals({
  apartmentSpecialDeals,
  listing,
}) {
  typeCheck('apartmentSpecialDeals::Array', apartmentSpecialDeals);
  typeCheck('listing::NonEmptyObject', listing);

  const nowMoment = moment();

  return apartmentSpecialDeals
    .filter((apartmentSpecialDeal) => {
      return (
        (
          !apartmentSpecialDeal.endDate
          || nowMoment.isBefore(apartmentSpecialDeal.endDate)
        )
        && (
          (
            !apartmentSpecialDeal.floorPlanId
            && apartmentSpecialDeal.applicableApartmentUnits.length === 0
          )
          || apartmentSpecialDeal.applicableApartmentUnits.includes(listing.apartmentUnit)
          || (
            apartmentSpecialDeal.floorPlanId
            && apartmentSpecialDeal.floorPlanId === listing.floorPlanId
          )
        )
      );
    });
}

export function getFloorPlanDisplayFields(floorPlan, units) {
  const showUnits = (units || [])
    .filter(({ apartmentUnit }) =>
      floorPlan.tourableApartmentUnits.includes(apartmentUnit)
    );

  const otherUnits = (units || [])
    .filter(({ status, apartmentUnit }) =>
      !floorPlan.tourableApartmentUnits.includes(apartmentUnit)
      && status === LISTING_STATUS_ACTIVE
    );

  return {
    id: floorPlan.id,
    displayName: floorPlan.displayName,
    photoUrls: floorPlan.photoUrls,
    price: showUnits[0] && showUnits[0].price,
    bedrooms: showUnits[0] && showUnits[0].bedrooms,
    bathrooms: showUnits[0] && showUnits[0].bathrooms,
    squareFeet: showUnits[0] && showUnits[0].squareFeet,
    showUnits,
    otherUnits,
  };
}
